// Color, label, and calendar constants shared across CI panels.
// Pure data — no React, no formatting logic.

// Short month names, indexed 0–11. Used by the date formatters in ./format and
// by panels that build their own month labels (e.g. AiPricingActionsPanel's range
// helper). Backend dates are plain "YYYY-MM-DD" strings parsed without Date() to
// dodge the UTC timezone shift, so this is the source of month names for CI.
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Day-of-week column descriptors for the DOW heatmap panels. `key` matches the
// backend's per-day average field; `short` is the column header label.
export const DOW_COLUMNS = [
  { key: "sun_avg", short: "Sun" },
  { key: "mon_avg", short: "Mon" },
  { key: "tue_avg", short: "Tue" },
  { key: "wed_avg", short: "Wed" },
  { key: "thu_avg", short: "Thu" },
  { key: "fri_avg", short: "Fri" },
  { key: "sat_avg", short: "Sat" },
];

// Categorical chart palette, ordered for maximum hue separation between adjacent
// slots: blue, emerald, pink, purple, amber, red, gray. Drawn from the spec hues.
// Used by pie/donut charts where each slice needs a distinct, stable color.
export const CATEGORY_PALETTE = ["#3B82F6", "#10B981", "#EC4899", "#8B5CF6", "#F59E0B", "#EF4444", "#9CA3AF"];

// The CI spec's named series hues (competitive_intelligence.md §"Color System"),
// keyed by HUE rather than role because each recurs under several role labels:
// blue = client / you / available / hotel-vs-airbnb-Hotel / RV / lower_rate;
// emerald = market / compSet / rate / Direct / Tent; red = sold-out / below-market;
// pink = airbnb / Glamping; gray = neutral / hotel-in-rate-trends / no-data.
// Panels alias these to their local role names (COLOR_YOU = SERIES_HUES.blue, …) so
// the literal hex lives in one place instead of being re-typed per panel.
export const SERIES_HUES = {
  blue: "#3B82F6",
  emerald: "#10B981",
  red: "#EF4444",
  pink: "#EC4899",
  gray: "#9CA3AF",
};

// Demand-signal tone mapping per spec §1.8 / §8 (E6). Keyword regex, NOT exact
// string match — the backend emits free-form values like "HIGH DEMAND",
// "ELEVATED", "RESTRICTED", "NORMAL", and may append suffixes. HIGH|CRITICAL|MAJOR
// → danger (red), ELEVATED → warning (yellow), RESTRICTED → caution (orange),
// else → neutral (gray). Used by every panel that surfaces a demand_signal /
// event_signal / status string derived from the demand classifier.
export function getDemandSignalTone(signal) {
  if (!signal) return "neutral";
  if (/HIGH|CRITICAL|MAJOR/.test(signal)) return "danger";
  if (/ELEVATED/.test(signal)) return "warning";
  if (/RESTRICTED/.test(signal)) return "caution";
  return "neutral";
}

// Tailwind class strings keyed by tone. Badge = inline pill; RowTint = full-row
// background tint for highlighted table rows. Both include dark-mode variants.
export const DEMAND_SIGNAL_TONE_CLASSES = {
  danger: {
    badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    row:   "bg-red-50/60 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20",
  },
  warning: {
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    row:   "bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20",
  },
  caution: {
    badge: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    row:   "bg-orange-50/60 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20",
  },
  neutral: {
    badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    row:   "hover:bg-gray-50 dark:hover:bg-gray-800/50",
  },
};
